import {
  Settings as SettingsIcon,
  LogIn,
  LogOut,
  CheckCircle2,
  CircleSlash,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  PageHeader,
} from '../../ui';
import { useAuth } from '../../hooks/useAuth';
import { getApiConfig, getCognitoConfig } from '../../config/apiConfig';

/** Small labeled key/value row. */
function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5 py-2">
      <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
        {label}
      </span>
      <span className="break-all font-mono text-sm text-zinc-200">{value}</span>
    </div>
  );
}

/**
 * Settings / Connection page. Shows the API base URL, auth status and identity,
 * and a Sign in / Sign out control. Requires an authenticated session (gated
 * like every other feature route).
 */
export function SettingsPage() {
  const { status, identity, signIn, signOut } = useAuth();

  const { baseUrl } = getApiConfig();
  const cognito = getCognitoConfig();

  const authed = status === 'authenticated';

  return (
    <div>
      <PageHeader
        icon={<SettingsIcon className="h-5 w-5" />}
        title="Settings & Connection"
        description="Backend endpoint and authentication."
      />

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Connection */}
        <Card>
          <CardHeader>
            <CardTitle>Connection</CardTitle>
          </CardHeader>
          <CardBody className="divide-y divide-surface-border">
            <InfoRow label="API base URL" value={baseUrl} />
            <InfoRow label="Cognito user pool" value={cognito.userPoolId} />
            <InfoRow label="App client" value={cognito.clientId} />
            <InfoRow label="Redirect URI" value={cognito.redirectUri} />
            <div className="flex flex-col gap-2 pt-3 text-xs text-zinc-500">
              <p>
                Generic document store (templates, rooms, tasks, estimates,
                goals, workstreams, changes, daily updates, blockers, documents,
                decisions, services, conversations) and Bedrock inference use the
                REAL backend.
              </p>
              <p>
                Code Knowledge (Spec Studio) uses the REAL backend; People
                (PhoneTool) is deferred and not wired.
              </p>
            </div>
          </CardBody>
        </Card>

        {/* Authentication */}
        <Card>
          <CardHeader>
            <CardTitle>Authentication</CardTitle>
            <Badge tone={authed ? 'success' : 'neutral'} dot>
              {status === 'loading'
                ? 'Checking…'
                : authed
                  ? 'Signed in'
                  : 'Signed out'}
            </Badge>
          </CardHeader>
          <CardBody className="space-y-4">
            <div className="flex items-center gap-3">
              {authed ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-400" />
              ) : (
                <CircleSlash className="h-5 w-5 text-zinc-500" />
              )}
              <div className="text-sm">
                {authed && identity ? (
                  <>
                    <span className="font-medium text-zinc-100">
                      {identity.name}
                    </span>
                    <span className="ml-1.5 text-zinc-500">
                      @{identity.alias}
                    </span>
                  </>
                ) : (
                  <span className="text-zinc-500">
                    Not signed in — the real backend will reject requests.
                  </span>
                )}
              </div>
            </div>

            <div>
              {authed ? (
                <Button
                  variant="outline"
                  leftIcon={<LogOut className="h-4 w-4" />}
                  onClick={signOut}
                >
                  Sign out
                </Button>
              ) : (
                <Button
                  leftIcon={<LogIn className="h-4 w-4" />}
                  onClick={signIn}
                >
                  Sign in with Midway
                </Button>
              )}
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

export default SettingsPage;
